'use client';

import {
  forwardRef,
  ForwardedRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import {
  type FeedbackBarProps,
  FeedbackBar,
} from '../../utils/components/FeedbackBar';
import './css/ImportantNote.scss';

export type ImportantNoteVariant = 'info' | 'warning';

export interface ImportantNoteProps
  extends Omit<FeedbackBarProps, 'renderIcon'> {
  variant?: ImportantNoteVariant;
  enableV4Updates?: boolean;
  /**
   * Sets the aria-label text for the info icon. Defaults to English for backward compatibility.
   */
  infoIconAriaLabel?: string;
  /**
   * Sets the aria-label text for the warning icon. Defaults to English for backward compatibility.
   */
  warningIconAriaLabel?: string;
}

const warningIcon = (ariaLabel: string, iconColor: string) => (
  <svg
    focusable="false"
    aria-label={ariaLabel}
    role="img"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_417_25)">
      <path
        d="M23.8877 20.0203L18.6459 10.8846L13.4041 1.74883C13.1045 1.29953 12.6552 1 12.0562 1C11.4571 1 11.0078 1.29953 10.7083 1.74883L5.46646 10.8846L0.224649 20.0203C-0.074883 20.4696 -0.074883 21.0686 0.224649 21.5179C0.524181 21.9672 0.973479 22.2668 1.57254 22.2668H12.0562H22.5398C23.1388 22.2668 23.5881 21.9672 23.8877 21.5179C24.0374 21.0686 24.0374 20.4696 23.8877 20.0203ZM13.1045 8.63807L12.6552 15.6771H11.1576L10.858 8.63807H13.1045ZM12.805 18.6724C12.6552 18.9719 12.3557 19.1217 11.9064 19.1217C11.6069 19.1217 11.3073 18.9719 11.0078 18.8222C10.858 18.6724 10.7083 18.3729 10.7083 17.9236C10.7083 17.624 10.858 17.3245 11.0078 17.025C11.1576 16.8752 11.4571 16.7254 11.9064 16.7254C12.2059 16.7254 12.5055 16.8752 12.805 17.025C12.9548 17.1747 13.1045 17.4743 13.1045 17.9236C13.1045 18.2231 12.9548 18.5226 12.805 18.6724Z"
        fill={iconColor}
      />
    </g>
    <defs>
      <clipPath id="clip0_417_25">
        <rect
          width="24"
          height="24"
          fill="white"
        />
      </clipPath>
    </defs>
  </svg>
);

const infoIcon = (ariaLabel: string) => (
  <svg
    className="ps-important-note-icon-info"
    focusable="false"
    aria-label={ariaLabel}
    role="img"
    width="24"
    height="24"
    viewBox="0 0 23 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.5 22.2812C5.60625 22.2812 0.71875 17.3937 0.71875 11.5C0.71875 5.60625 5.60625 0.71875 11.5 0.71875C17.3937 0.71875 22.2812 5.60625 22.2812 11.5C22.2812 17.3937 17.3937 22.2812 11.5 22.2812ZM9.34375 16.1C8.9125 16.1 8.48125 16.5312 8.48125 16.9625V17.25C8.48125 17.6812 8.9125 18.1125 9.34375 18.1125H13.5125C13.9437 18.1125 14.375 17.6812 14.375 17.25V16.9625C14.375 16.5312 13.9437 16.1 13.5125 16.1C13.5125 16.1 13.5125 16.1 13.3688 16.1C13.3688 15.9563 13.3688 15.9562 13.3688 15.6687V9.4875C13.5125 9.05625 13.3687 8.76875 12.9375 8.625C12.9375 8.625 12.9375 8.625 13.0813 8.625C13.5125 8.19375 13.6562 7.61875 13.6562 7.04375C13.6562 6.46875 13.3688 5.89375 13.0813 5.4625C12.65 5.03125 12.075 4.8875 11.5 4.8875C10.925 4.8875 10.35 5.175 9.91875 5.4625C9.4875 5.89375 9.34375 6.46875 9.34375 7.04375C9.34375 7.61875 9.63125 8.19375 9.91875 8.625H9.34375C8.9125 8.625 8.48125 9.05625 8.48125 9.4875V9.775C8.48125 10.2063 8.9125 10.6375 9.34375 10.6375C9.34375 10.6375 9.34375 10.6375 9.4875 10.6375C9.4875 10.7812 9.4875 10.7812 9.4875 11.0687V15.525C9.4875 15.8125 9.4875 15.9563 9.34375 16.1Z" />
    <path d="M11.5 1.4375C17.1063 1.4375 21.5625 5.89375 21.5625 11.5C21.5625 17.1063 17.1063 21.5625 11.5 21.5625C5.89375 21.5625 1.4375 17.1063 1.4375 11.5C1.4375 5.89375 5.89375 1.4375 11.5 1.4375ZM8.76875 8.05C8.19375 8.3375 7.7625 8.9125 7.7625 9.4875V9.775C7.7625 10.35 8.19375 10.925 8.625 11.2125V15.3812C8.05 15.6687 7.7625 16.2437 7.7625 16.8187V17.25C7.7625 18.1125 8.48125 18.8313 9.34375 18.8313H13.5125C14.375 18.8313 15.0938 18.1125 15.0938 17.25V16.9625C15.0938 16.3875 14.6625 15.8125 14.0875 15.525V9.4875C14.0875 9.2 13.9438 8.76875 13.8 8.48125C14.2313 8.05 14.375 7.61875 14.375 7.04375C14.375 6.325 14.0875 5.60625 13.5125 5.03125C12.9375 4.45625 12.3625 4.16875 11.5 4.16875C10.7812 4.16875 10.0625 4.45625 9.4875 5.03125C8.9125 5.60625 8.625 6.325 8.625 7.04375C8.625 7.33125 8.625 7.7625 8.76875 8.05ZM11.5 0C5.175 0 0 5.175 0 11.5C0 17.825 5.175 23 11.5 23C17.825 23 23 17.825 23 11.5C23 5.175 17.825 0 11.5 0ZM11.5 8.48125C11.0687 8.48125 10.7813 8.3375 10.4938 8.05C10.2063 7.7625 10.0625 7.475 10.0625 7.04375C10.0625 6.6125 10.2063 6.325 10.4938 6.0375C10.7813 5.75 11.0687 5.60625 11.5 5.60625C11.9313 5.60625 12.2187 5.75 12.5062 6.0375C12.7937 6.325 12.9375 6.6125 12.9375 7.04375C12.9375 7.475 12.7937 7.7625 12.5062 8.05C12.2187 8.3375 11.9313 8.48125 11.5 8.48125ZM9.34375 17.3938C9.2 17.3938 9.2 17.25 9.2 17.25V16.9625C9.2 16.8188 9.34375 16.8187 9.34375 16.8187C9.63125 16.8187 9.775 16.675 9.91875 16.5312C9.91875 16.5312 10.0625 16.3875 10.0625 15.6687V11.0687C10.2063 10.4937 10.0625 10.2062 10.0625 10.2062C9.91875 10.0625 9.775 10.0625 9.34375 9.91875C9.2 9.91875 9.2 9.775 9.2 9.775V9.4875C9.2 9.34375 9.34375 9.34375 9.34375 9.34375H12.65C12.7938 9.34375 12.7937 9.4875 12.7937 9.4875V15.6687C12.7937 16.3875 12.9375 16.5312 12.9375 16.5312C12.9375 16.675 13.225 16.675 13.5125 16.675C13.6562 16.675 13.6562 16.8187 13.6562 16.8187V17.25C13.6562 17.3938 13.5125 17.3938 13.5125 17.3938H9.34375Z" />
  </svg>
);

const v4InfoIcon = (ariaLabel: string) => (
  <svg
    className="ps-important-note-icon-info"
    focusable="false"
    aria-label={ariaLabel}
    role="img"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_67919_2075)">
      <path
        d="M11.9851 0C5.35511 0 -0.0148926 5.37 -0.0148926 12C-0.0148926 18.63 5.35511 24 11.9851 24C18.6151 24 23.9851 18.63 23.9851 12C23.9851 5.37 18.6151 0 11.9851 0ZM10.4551 5.175C10.7551 4.875 11.1151 4.725 11.5351 4.725C11.9551 4.725 12.3301 4.875 12.6301 5.175C12.9301 5.475 13.0651 5.835 13.0651 6.27C13.0651 6.705 12.9151 7.05 12.6301 7.35C12.3301 7.65 11.9701 7.8 11.5351 7.8C11.1001 7.8 10.7551 7.65 10.4551 7.35C10.1551 7.05 10.0051 6.69 10.0051 6.27C10.0051 5.85 10.1551 5.475 10.4551 5.175ZM14.9851 18.21C14.8201 18.36 14.6101 18.45 14.3701 18.45H9.58511C9.34511 18.45 9.13511 18.375 8.97011 18.21C8.80511 18.06 8.73011 17.85 8.73011 17.625C8.73011 17.4 8.80511 17.19 8.97011 17.04C9.13511 16.89 9.34511 16.8 9.58511 16.8H11.0401V11.4H9.78011C9.54011 11.4 9.33011 11.325 9.16511 11.16C9.00011 11.01 8.92511 10.8 8.92511 10.575C8.92511 10.35 9.00011 10.14 9.16511 9.99C9.33011 9.84 9.54011 9.75 9.78011 9.75H11.9851C12.2851 9.75 12.5101 9.84 12.6601 10.035C12.8101 10.23 12.9001 10.485 12.9001 10.8V16.785H14.3551C14.5951 16.785 14.8051 16.86 14.9701 17.025C15.1351 17.175 15.2101 17.37 15.2101 17.61C15.2101 17.85 15.1351 18.045 14.9701 18.195L14.9851 18.21Z"
        fill="#474644"
      />
    </g>
    <defs>
      <clipPath id="clip0_67919_2075">
        <rect
          width="24"
          height="24"
          fill="white"
        />
      </clipPath>
    </defs>
  </svg>
);

export const ImportantNote = forwardRef(
  (props: ImportantNoteProps, outerRef: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      className,
      renderHeading,
      variant = 'info',
      enableV4Updates = false,
      infoIconAriaLabel = 'Information: ',
      warningIconAriaLabel = 'Warning: ',
      closeButtonAriaLabel,
      onClose,
      ...spreadProps
    } = props;
    const innerRef = useRef<HTMLDivElement>(null);

    const importantNoteClasses = classNames({
      'ps-important-note': true,
      'ps-important-note-warning': variant === 'warning',
      'ps-important-note-v4-enabled': enableV4Updates,
      [`${className}`]: className,
    });

    /* Points the outerRef to the innerRef used by the FeedbackBar.
      This allows us to use our own ref handle to query the DOM,
      while ensuring that consumers have access to the same node. */
    useImperativeHandle(outerRef, () => innerRef.current!, []);

    useEffect(() => {
      const hasHeading = () => {
        return (
          innerRef.current?.querySelector('h1') ||
          innerRef.current?.querySelector('h2') ||
          innerRef.current?.querySelector('h3') ||
          innerRef.current?.querySelector('h4') ||
          innerRef.current?.querySelector('h5') ||
          innerRef.current?.querySelector('h6')
        );
      };

      if (enableV4Updates) {
        if (renderHeading) {
          console.warn(
            'The renderHeading prop for <ImportantNote> has been deprecated and will be removed in Pulse Web v5. Please update your implementation in preparation for this major release. Continued use of the renderHeading prop may result in accessibility defects.'
          );
        }

        if (!renderHeading && hasHeading()) {
          console.warn(
            'The use of headings in <ImportantNote> is strongly discouraged. To avoid accessibility defects, please update your implementation by removing heading elements.'
          );
        }
      }
    }, [enableV4Updates, renderHeading]);

    const getIcon = () => {
      if (variant === 'info') {
        return enableV4Updates
          ? v4InfoIcon(infoIconAriaLabel)
          : infoIcon(infoIconAriaLabel);
      }

      return warningIcon(
        warningIconAriaLabel,
        enableV4Updates ? '#CC6E00' : '#E07800'
      );
    };

    return (
      <FeedbackBar
        {...spreadProps}
        className={importantNoteClasses}
        renderHeading={renderHeading}
        renderIcon={getIcon()}
        ref={innerRef}
        onClose={onClose}
        closeButtonAriaLabel={closeButtonAriaLabel}
      >
        {children}
      </FeedbackBar>
    );
  }
);
